/**
 * Upgrade-gate catalogue — one source of truth for what's gated, by whom,
 * and with what copy.
 *
 * Every tier-gated UX surface reads from this file:
 *
 * - Frontend `useUpgradeGate(gateKey, context)` — pre-emptive disable + tooltip
 * - Frontend `<UpgradePromptModal>` — full-screen modal after a hard denial
 * - Frontend 402 API interceptor — maps backend error codes → gates
 *
 * Backend enforcement returns a matching `code` from `GATE_ERROR_CODE` on
 * 402 responses so the frontend interceptor knows which gate fired.
 *
 * Adding a new gate:
 * 1. Add a `GateKey` to the literal union
 * 2. Add an entry to `GATES` with check + copy
 * 3. Add a backend error code to `GATE_ERROR_CODE` and emit it from the
 *    service-layer check that enforces this gate.
 *
 * The `check` callbacks are pure — they take `(tier, context)` so the
 * frontend can pre-render disabled states without a server roundtrip.
 * Server-side enforcement uses the backend's `WorkspacePolicyService`,
 * which loads the workspace, derives the same context, and re-runs the
 * gate's `check` for the authoritative answer.
 */
import { FEATURE_REGISTRY } from './feature-registry';
import { tierPolicy } from './policy';
import { formatRetentionWindow, nextSellableTierAbove } from './tiers';
/**
 * The tier the upgrade funnel should name for a free user. Derived, so
 * retiring a tier moves the funnel with the copy instead of leaving the two
 * describing different plans.
 */
const SELLABLE_UPGRADE_TIER = nextSellableTierAbove('FREE') ?? 'TEAM';
/**
 * The plan name to put in upsell copy: the next tier the customer can actually
 * BUY, never simply the next one that unlocks the feature.
 *
 * `requiredTier` on each gate answers a different question (which tier the
 * feature is gated at) and is not a safe source for copy: `publicSharing`
 * unlocks at PRO, and PRO is retired, so naming it sent Starter users to a
 * screen where `PlanSelector` would not sell it.
 */
function upgradeTargetName(tier) {
    const target = nextSellableTierAbove(tier);
    return target ? tierPolicy(target).displayName : 'a paid plan';
}
// ---------------------------------------------------------------------------
// Pure check helpers — small inline functions so the gate `check` callbacks
// don't repeat the same boolean. Anything more complex lives in the backend
// policy resolver.
// ---------------------------------------------------------------------------
function legacyTotalMemberAllowance(tier, paidSeats) {
    const policy = tierPolicy(tier);
    // Maximum total members = max paid seats (or current quantity on Team)
    // plus the effective guest cap.
    const paidCap = policy.billing.maxSeats ?? paidSeats;
    const guestCap = policy.guests.maxTotal ?? (policy.guests.perPaidSeat ?? 0) * Math.max(1, paidSeats);
    return paidCap + guestCap;
}
function aiQuotaBaseLimit(tier, feature) {
    return tierPolicy(tier).aiQuotas[feature].limit;
}
function describeAiQuotaAllowance(tier, feature) {
    const spec = tierPolicy(tier).aiQuotas[feature];
    if (spec.limit === null)
        return 'unlimited usage';
    const noun = spec.limit === 1 ? spec.nounSingular : spec.nounPlural;
    return `${spec.limit.toLocaleString()} ${noun}`;
}
// ---------------------------------------------------------------------------
// GATES — registry
// ---------------------------------------------------------------------------
export const GATES = {
    'invite-member': {
        key: 'invite-member',
        severity: 'hard',
        requiredTier: 'PRO',
        check: (tier, ctx) => {
            const cap = legacyTotalMemberAllowance(tier, ctx.paidSeatCount ?? 1);
            if (cap === null)
                return true;
            return (ctx.memberCount ?? 0) < cap;
        },
        title: "You've reached your member limit",
        body: (tier) => {
            const policy = tierPolicy(tier);
            const seats = policy.billing.maxSeats;
            const guests = policy.guests.maxTotal;
            if (seats === 1 && guests !== null) {
                return `Your ${policy.displayName} plan includes 1 paid seat and ${guests} guest${guests === 1 ? '' : 's'} per workspace. Upgrade to add more paid seats.`;
            }
            return `Your ${policy.displayName} plan has reached its member limit. Upgrade or add more paid seats.`;
        },
        cta: 'Upgrade to invite more',
    },
    'create-workspace': {
        key: 'create-workspace',
        severity: 'soft',
        requiredTier: 'PRO',
        // Workspace creation is no longer capped programmatically — additional
        // workspaces are gated through support so we can learn the use case.
        check: () => true,
        title: 'Need another workspace?',
        body: () => 'Additional workspaces are provisioned through support so we can match the right setup. Get in touch and we will sort it.',
        cta: 'Contact support',
    },
    'enable-phi': {
        key: 'enable-phi',
        severity: 'hard',
        // PHI is TEAM-only (features.phi true only on TEAM), so the upgrade target
        // is Team — matches the body/cta copy and minimumTierForFeature('phi').
        requiredTier: 'TEAM',
        check: (tier) => tierPolicy(tier).features.phi,
        title: 'Restricted mode requires a paid plan',
        body: (tier) => `Your ${tierPolicy(tier).displayName} plan does not include Restricted mode (HIPAA-ready). Upgrade to Team to enable HIPAA-ready handling of protected health information.`,
        cta: 'Upgrade to Team',
    },
    'storage-upload': {
        key: 'storage-upload',
        severity: 'hard',
        requiredTier: 'PRO',
        check: (_tier, ctx) => {
            // Caller is responsible for passing the EFFECTIVE quota
            // (already multiplied by paidSeatCount on per-seat tiers).
            // Backend `WorkspacePolicyService.canUploadStorage` does that.
            const quota = ctx.quotaBytes ?? Number.POSITIVE_INFINITY;
            const current = ctx.currentBytes ?? 0;
            const addl = ctx.additionalBytes ?? 0;
            return current + addl <= quota;
        },
        title: 'Storage limit reached',
        body: (tier) => {
            const spec = tierPolicy(tier).storage;
            const gb = Math.round(spec.bytesPerSeat / (1024 * 1024 * 1024));
            const suffix = spec.pooled ? ' per paid seat, pooled' : '';
            return `Your ${tierPolicy(tier).displayName} plan includes ${gb} GB of storage${suffix}. Upgrade for more space.`;
        },
        cta: 'Upgrade plan',
    },
    'audit-beyond-retention': {
        key: 'audit-beyond-retention',
        severity: 'soft',
        requiredTier: 'TEAM',
        // TODO(audit-log plan Task 6b, D3): this gate is DEAD. No tier declares
        // `auditDays: null`, so `check` is false everywhere and the gate never
        // grants. Since retention became a read-time view window, the intended
        // predicate is probably
        // `auditWindowDays(tier) >= AUDIT_ABSOLUTE_RETENTION_DAYS` (true for TEAM
        // only), and the `days === null` branch in `body` below is dead with it.
        // Behaviour deliberately left unchanged here: flipping a gate that
        // currently grants to nobody changes what paying customers are shown, and
        // that is David's call, not this task's. Tracked at
        // `docs/planning/audit-log-chain-of-custody.md`, Task 6b Step 6.
        check: (tier) => tierPolicy(tier).retention.auditDays === null,
        title: 'Older audit history needs an upgrade',
        body: (tier) => {
            const days = tierPolicy(tier).retention.auditDays;
            if (days === null)
                return 'Full audit history is available on this plan.';
            const years = Math.round(days / 365);
            const window = years >= 1 ? `${years} year${years === 1 ? '' : 's'}` : `${days} days`;
            return `Your ${tierPolicy(tier).displayName} plan keeps the most recent ${window} of audit history. Upgrade to Team for unlimited retention.`;
        },
        cta: 'Upgrade to unlock history',
    },
    'share-artifact': {
        key: 'share-artifact',
        severity: 'hard',
        // The tier we TELL the user they need, so it must be one they can buy.
        // Read by the two upgrade-funnel events only (`useUpgradeGate`,
        // `UpgradePromptModal`); it drives no enforcement. Left at 'PRO' it
        // reported Individual demand for a CTA that says Team.
        requiredTier: SELLABLE_UPGRADE_TIER,
        check: (tier) => tierPolicy(tier).features.publicSharing,
        title: 'Public sharing requires a paid plan',
        body: (tier) => `Your ${tierPolicy(tier).displayName} plan doesn't include public share links. Upgrade to ${upgradeTargetName(tier)} to share any note, protocol or artifact via a public link.`,
        // Phrased like the sibling history gate rather than naming a tier: a static
        // string cannot track `sellable`, which is how this one came to say
        // "Upgrade to Individual" months after Individual stopped being sold.
        cta: 'Upgrade to unlock sharing',
    },
    'version-history-beyond-retention': {
        key: 'version-history-beyond-retention',
        severity: 'soft',
        // The tier we TELL the user they need, so it must be one they can buy.
        // Read by the two upgrade-funnel events only (`useUpgradeGate`,
        // `UpgradePromptModal`); it drives no enforcement. Left at 'PRO' it
        // reported Individual demand for a CTA that says Team.
        requiredTier: SELLABLE_UPGRADE_TIER,
        check: () => true, // soft: never hard-blocks, only informs
        title: 'Some history is hidden',
        body: (tier) => {
            const days = tierPolicy(tier).retention.snapshotDays;
            if (days === null)
                return 'Full version history is available on this plan.';
            const target = nextSellableTierAbove(tier);
            if (target === null) {
                return `Your ${tierPolicy(tier).displayName} plan includes ${formatRetentionWindow(days)} of version history.`;
            }
            // Type-required, not reachable with today's tiers: every tier has a
            // numeric snapshotDays. Kept because the field is `number | null`, so TS
            // forces whoever ships a null window to come here and write the sentence
            // rather than get "for unlimited of version history" for free. Do NOT
            // write a test asserting this branch's output: it cannot fail.
            const targetDays = tierPolicy(target).retention.snapshotDays;
            const offer = targetDays === null
                ? `Upgrade to ${tierPolicy(target).displayName} for unlimited version history.`
                : `Upgrade to ${tierPolicy(target).displayName} for ${formatRetentionWindow(targetDays)}.`;
            return `Your ${tierPolicy(tier).displayName} plan includes ${formatRetentionWindow(days)} of version history. ${offer}`;
        },
        cta: 'Upgrade to see full history',
    },
    'connect-toolkit': {
        // The cap lives at `TIER_POLICY[tier].connectors.maxPerUser`
        // (0 = disabled, null = unlimited, number = hard cap). The frontend
        // gate is informational only — the backend's `assertCanConnect` in
        // `services/connectors/connection.service.ts` is the enforcement
        // point and emits `TOOLKIT_CAP_REACHED`, which routes through this
        // gate's copy via `GATE_ERROR_CODE`.
        key: 'connect-toolkit',
        severity: 'hard',
        // The tier we TELL the user they need, so it must be one they can buy.
        // Read by the two upgrade-funnel events only (`useUpgradeGate`,
        // `UpgradePromptModal`); it drives no enforcement. Left at 'PRO' it
        // reported Individual demand for a CTA that says Team.
        requiredTier: SELLABLE_UPGRADE_TIER,
        check: () => true,
        title: "You've reached your connector limit",
        body: (tier) => {
            const cap = tierPolicy(tier).connectors.maxPerUser;
            if (cap === 0) {
                return `Your ${tierPolicy(tier).displayName} plan doesn't include connectors. Upgrade to ${upgradeTargetName(tier)} for federated search across Notion, Slack and more.`;
            }
            return `Your ${tierPolicy(tier).displayName} plan has reached its connector limit. Upgrade for more.`;
        },
        cta: 'Upgrade to connect more',
    },
    'ai-chat-quota': aiQuotaGate('ai-chat-quota', 'chat', "You've used your monthly chat quota"),
    'ai-live-quota': aiQuotaGate('ai-live-quota', 'liveMinutes', "You've used your monthly live mode quota"),
    'ai-text-extraction-quota': aiQuotaGate('ai-text-extraction-quota', 'textExtractions', "You've used your monthly text-extraction quota"),
    'ai-transcription-quota': aiQuotaGate('ai-transcription-quota', 'transcriptionMinutes', "You've used your monthly transcription quota"),
    'ai-code-execution-quota': aiQuotaGate('ai-code-execution-quota', 'codeExecutionMinutes', "You've used your monthly code-analysis quota"),
    'tier-feature': {
        // Generic gate for every TierFeature literal that doesn't have a
        // bespoke gate. The upgrade-modal copy is pulled from
        // FEATURE_REGISTRY at render time so adding a new feature is one
        // registry entry — the gate config doesn't change. Pass the
        // feature key in `GateContext.featureKey`.
        key: 'tier-feature',
        severity: 'hard',
        requiredTier: 'PRO',
        check: (tier, ctx) => {
            if (!ctx.featureKey)
                return true;
            return tierPolicy(tier).features[ctx.featureKey];
        },
        title: 'Upgrade to unlock this feature',
        body: (tier, ctx) => {
            const featureKey = ctx?.featureKey;
            if (!featureKey) {
                return `Upgrade your ${tierPolicy(tier).displayName} plan to unlock this feature.`;
            }
            const reg = FEATURE_REGISTRY[featureKey];
            return `Your ${tierPolicy(tier).displayName} plan doesn't include ${reg.displayName}. Upgrade to ${reg.description}.`;
        },
        cta: 'Upgrade plan',
    },
};
/**
 * AI-quota gates share identical structure (hard severity, same upgrade
 * target). Each gate is a thin wrapper that pulls the workspace's
 * effective cap from `ctx.aiQuotaCap` (preferred — already multiplied by
 * paidSeatCount on per-seat tiers) or falls back to the policy's BASE
 * limit. Body copy says "per paid seat / per period" on per-seat tiers.
 *
 * **These gates do NOT consult `enforced`, and today that is safe only
 * because nothing wires them to live usage.** No `useUpgradeGate` call site
 * passes `aiQuotaCap` or `aiQuotaUsed`, so `check` runs against the policy
 * BASE limit with `used = 0` and cannot refuse anyone. Wiring a real
 * workspace's counters in here without also passing the dimension's
 * `enforced` flag would reproduce, in the UI, the bug that
 * `agent-service/services/quota_gate.py` was written to close: refusing a
 * workspace on a cap the backend is not enforcing
 * (`AI_QUOTA_ENFORCE_DEFAULT` is off in production). The frontend has
 * already paid for this once — see the comment in `ai-quota.service.ts`
 * about the 80% toast telling users to upgrade to escape a limit that did
 * not exist. The agent's structural guard is Python-only and cannot see
 * this file, so this note is the control.
 */
function aiQuotaGate(key, feature, title) {
    return {
        key,
        severity: 'hard',
        requiredTier: 'PRO',
        check: (tier, ctx) => {
            const cap = ctx.aiQuotaCap ?? aiQuotaBaseLimit(tier, feature);
            if (cap === null)
                return true;
            const used = ctx.aiQuotaUsed ?? 0;
            const units = ctx.aiQuotaUnits ?? 1;
            return used + units <= cap;
        },
        title,
        body: (tier) => {
            const policy = tierPolicy(tier);
            const spec = policy.aiQuotas[feature];
            if (spec.limit === null) {
                return `Your ${policy.displayName} plan includes unlimited usage.`;
            }
            const perWhat = spec.perSeat ? 'per paid seat, per period' : 'per period';
            return `Your ${policy.displayName} plan includes ${describeAiQuotaAllowance(tier, feature)} ${perWhat}. Upgrade for more.`;
        },
        cta: 'Upgrade plan',
    };
}
/**
 * Backend error codes that the 402 response body should emit.
 * Kept as a typed const so TypeScript catches drift between the backend
 * service check and the frontend gate mapping.
 */
export const GATE_ERROR_CODE = {
    // Legacy / bespoke gates with hand-authored upgrade-modal copy
    MEMBER_LIMIT: 'invite-member',
    WORKSPACE_LIMIT: 'create-workspace',
    PHI_TIER_REQUIRED: 'enable-phi',
    STORAGE_LIMIT: 'storage-upload',
    AUDIT_RETENTION_LIMIT: 'audit-beyond-retention',
    PUBLIC_SHARING_TIER_REQUIRED: 'share-artifact',
    SNAPSHOT_RETENTION_LIMIT: 'version-history-beyond-retention',
    TOOLKIT_CAP_REACHED: 'connect-toolkit',
    AI_CHAT_QUOTA_EXHAUSTED: 'ai-chat-quota',
    AI_LIVE_QUOTA_EXHAUSTED: 'ai-live-quota',
    AI_TEXT_EXTRACTION_QUOTA_EXHAUSTED: 'ai-text-extraction-quota',
    AI_TRANSCRIPTION_QUOTA_EXHAUSTED: 'ai-transcription-quota',
    AI_CODE_EXECUTION_QUOTA_EXHAUSTED: 'ai-code-execution-quota',
    // Generic tier-feature gates. The frontend interceptor reads the
    // feature key from the 402 response's `error.feature` field and
    // looks up copy from FEATURE_REGISTRY. The architecture test in
    // packages/shared/src/billing/__tests__/feature-registry.test.ts
    // asserts every FEATURE_REGISTRY entry's `errorCode` appears here.
    INFERRED_RELATIONSHIPS_TIER_REQUIRED: 'tier-feature',
    QUALITY_ASSESSMENT_TIER_REQUIRED: 'tier-feature',
    PERSONAL_MEMORY_TIER_REQUIRED: 'tier-feature',
    WORKSPACE_INSTRUCTIONS_TIER_REQUIRED: 'tier-feature',
    KNOWLEDGE_GRAPH_TIER_REQUIRED: 'tier-feature',
    TRASH_RESTORE_TIER_REQUIRED: 'tier-feature',
    PRIVACY_CONTROLS_TIER_REQUIRED: 'tier-feature',
    WORKSPACE_ROLES_TIER_REQUIRED: 'tier-feature',
    COMPLIANCE_LOCKDOWN_TIER_REQUIRED: 'tier-feature',
    PRIORITY_SUPPORT_TIER_REQUIRED: 'tier-feature',
    // The generic `FEATURE_REQUIRED` code emitted by `requireFeature`
    // middleware also routes through the tier-feature gate.
    FEATURE_REQUIRED: 'tier-feature',
};
export function gateForErrorCode(code) {
    return Object.hasOwn(GATE_ERROR_CODE, code) ? GATE_ERROR_CODE[code] : null;
}
/**
 * The gate code an invitation-accept refusal carries in the ADD_MEMBER_ERROR
 * envelope's `details.cause` when the workspace has no free paid seat.
 * Deliberately NOT the response's outer `code`: MEMBER_LIMIT maps to HTTP 402,
 * whose frontend interceptor pops the admin-targeted upgrade modal — the
 * wrong audience for an invitee. Every invitee-facing accept surface
 * (OnboardingV2, App.tsx deep-link, VerifyEmail) branches on this constant;
 * typing it against GATE_ERROR_CODE makes a backend gate-code rename a
 * compile error here instead of a silent fallback to the generic dead-end.
 */
export const SEAT_LIMIT_ACCEPT_CAUSE = 'MEMBER_LIMIT';
//# sourceMappingURL=gates.js.map